import { NovatiqMessageService } from '@/layouts/service/message.service';
import { ZenithManagementService } from '@/features/administration/zenith-management/data/zenith-management.service';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { IUpdatePriceBand } from '../../../features/administration/zenith-management/zenith-management.model';

@Component({
  selector: 'novatiq-price-band-form',
  templateUrl: './price-band-form.component.html',
  styleUrls: ['./price-band-form.component.scss'],
  providers: [ConfirmationService],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TableModule,
    InputNumberModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule
  ]
})
export class PriceBandFormComponent implements OnInit {
  @Input() parentForm: FormGroup;
  @Input() Currency: string;
  @Input() isEditMode: boolean;
  @Input() bandType: string;
  @Input() isReadOnly: boolean;
  @Input() showDiscountedPrice: boolean;
  @Output() bandUpdate = new EventEmitter<string>();

  PublisherType: string;
  lastUpperLimit = 0;
  priceBandFormGroup: FormGroup;
  readonly MAX_PRICE_PER_USER = 99999.99;
  readonly MAX_UPPER_LIMIT_COUNT = 999999999;

  private readonly formBuilder = inject(FormBuilder);
  private readonly zenithManagementService = inject(ZenithManagementService);
  private readonly toaster = inject(NovatiqMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  readonly translate = inject(TranslateService);

  get priceBandFormArray() {
    return this.parentForm.get(this.bandType) as FormArray;
  }

  get priceBandFormArrayControls() {
    return this.parentForm.get(this.bandType)?.['controls'];
  }

  get zenithPriceSetupId() {
    return this.parentForm.get('id')?.value;
  }

  ngOnInit(): void {
    this.PublisherType = this.parentForm.get('publisherType')?.value;
    if (!this.priceBandFormArray) {
      console.error('parent should have this.');
    }
  }

  getLastIndex() {
    return this.priceBandFormArrayControls.length - 1;
  }

  getLastUpperLimitValue(i: number) {
    return this.priceBandFormArrayControls[i - 1].get('upperLimitUserCount')?.value;
  }

  getLowerLimitValue(i: number) {
    return this.priceBandFormArrayControls[i].get('lowerLimitUserCount')?.value;
  }

  addPriceBand() {
    const lastPriceBand = this.priceBandFormArrayControls[this.priceBandFormArrayControls.length - 2];
    const lastUpperLimitValue = lastPriceBand.get('upperLimitUserCount')?.value;
    const priceBandFields: Record<string, unknown> = {
      lowerLimitUserCount: [lastUpperLimitValue + 1, Validators.required],
      upperLimitUserCount: [0, [Validators.required]],
      pricePerUser: [0, [Validators.required]]
    };
    if (this.isEditMode) {
      priceBandFields.id = (<typeof crypto>crypto).randomUUID();
      priceBandFields.zenithPriceSetupID = this.zenithPriceSetupId;
    }
    const elementToAdd = this.formBuilder.group(priceBandFields);
    const secondLastIndex = this.priceBandFormArrayControls.length - 1;
    this.priceBandFormArray.insert(secondLastIndex, elementToAdd);
  }

  confirmPriceBandDeletion(index: number) {
    this.confirmationService.confirm({
      message: this.translate.instant('priceBandDeleteMessage1'),
      header: this.translate.instant('delete_confirm'),
      icon: 'pi pi-info-circle',
      accept: () => {
        const priceBandId = this.priceBandFormArrayControls[index].get('id').value;
        this.onRemovePriceBand(priceBandId);
      },
      reject: () => {
        console.info('user cancelled/closed the priceband dialog');
      }
    });
  }

  removePriceBand(index: number) {
    if (this.isEditMode) {
      this.confirmPriceBandDeletion(index);
    } else if (this.priceBandFormArray.length > 1) {
      if (this.priceBandFormArray.length > index) {
        let previousBandUpperLimit = 0;
        if (index > 0) {
          previousBandUpperLimit = this.priceBandFormArrayControls[index - 1].get('upperLimitUserCount').value + 1;
        }
        const nextBandLowerLimit = this.priceBandFormArrayControls[index + 1].get('lowerLimitUserCount');
        nextBandLowerLimit.setValue(previousBandUpperLimit);
      }
      this.priceBandFormArray.removeAt(index);
    }
  }

  onRemovePriceBand(priceBandId: string) {
    this.zenithManagementService.deletePriceBand(priceBandId).subscribe(
      () => {
        this.toaster.sendMessage(this.translate.instant('delete_success'), 'success');
        this.bandUpdate.emit('updateSuccess');
      },
      (error) => {
        this.showErrorMessage(error?.message);
      }
    );
  }

  upperLimitChange(index: number, priceBand: FormGroup) {
    if (index < this.priceBandFormArray.length - 1) {
      const upperLimit = this.priceBandFormArrayControls[index].get('upperLimitUserCount').value + 1;
      const nextBandLowerLimit = this.priceBandFormArrayControls[index + 1].get('lowerLimitUserCount');
      nextBandLowerLimit.setValue(upperLimit);
    }

    if (priceBand.value.lowerLimitUserCount > priceBand.value.upperLimitUserCount) {
      console.error('Upper limit cannot be less than lower limit.');
    }
  }

  onConfirmUpdate() {
    this.confirmationService.confirm({
      message: this.translate.instant('priceband_update_confirm') + '<br>' + this.translate.instant('confirmContinue'),
      header: this.translate.instant('confirm'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.onUpdate();
      },
      reject: (type) => {
        console.info('user cancelled/closed the priceband dialog', type);
      }
    });
  }

  onUpdate() {
    if (this.priceBandFormArray.valid) {
      const scheduledPriceBands = this.priceBandFormArray.value?.map(
        ({ upperLimitUserCount, startDate, endDate, discountedPricePerUser, ...keepAttrs }) => keepAttrs
      );

      const updatePriceBand: IUpdatePriceBand = {
        schedule: scheduledPriceBands
      };

      this.zenithManagementService.updateZenithPriceBand(updatePriceBand).subscribe(
        () => {
          this.toaster.sendMessage(this.translate.instant('priceUpdated'), 'success');
          this.bandUpdate.emit('updateSuccess');
        },
        (error) => {
          this.showErrorMessage(error?.message);
        }
      );
    }
  }

  showErrorMessage(message: string) {
    this.toaster.sendMessage(message, 'error');
  }
}
